const base = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

async function decode(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') ?? '';
  return contentType.includes('application/json') ? response.json() : response.text();
}

const statusMessages: Record<number, string> = {
  400: '请求内容不正确',
  401: '登录状态无效，请重新登录',
  403: '当前账号没有执行此操作的权限',
  404: '请求的数据不存在',
  409: '数据已发生变化，请刷新后重试',
  429: '操作过于频繁，请稍后重试',
  500: '服务器处理失败',
  503: '服务暂时不可用',
};

const errorCodeMessages: Record<string, string> = {
  invalid_password: '密码需要 12–256 个字符',
  order_price_pending: '请先补录应收金额再完成订单',
  order_pricing_locked: '订单已定价、已完成或已作废，请刷新后查看',
  price_cannot_be_unset: '已确定的金额不能改回待定价',
  invalid_role_update: '请检查权限选项并填写修改原因',
  role_not_found: '所选角色不存在，请刷新后重新选择',
  role_admin_access_required: '管理员角色及自己的角色必须保留用户管理权限',
  invalid_income: '请检查收入金额、收款人、方式和发生时间',
  income_request_conflict: '提交编号对应的收入内容不一致，请刷新后重试',
  invalid_agent_package: '请检查版本号、通道和 EXE 安装包大小',
  package_hash_mismatch: '安装包校验失败，请重新上传',
  release_not_newer: '此通道已发布相同或更高版本',
  source_note_required: '请填写来源锁定备注',
  stock_sku_exists: '商品编号已存在，请使用其他编号',
  stock_insufficient: '库存不足，请刷新后核对出库数量',
  stock_request_conflict: '此提交编号已用于其他库存变动，请重新打开操作',
  stock_item_inactive: '商品已停用，请重新启用后登记库存变动',
  invalid_stock_correction: '请填写商品名称、有效补货阈值和修改原因',
  invalid_stock_item: '请检查商品编号、名称、单位和补货阈值',
  invalid_stock_movement: '请填写有效数量和原因，数量最多保留四位小数',
  amount_due_below_received: '订单应收金额不能低于已收金额',
  expense_already_voided: '该支出已经作废，不能再修改',
  invalid_expense_correction: '支出修正内容或修正原因不完整',
  invalid_order_correction: '订单修正内容或修正原因不完整',
  invalid_payment: '请填写有效金额、收款方式和收款人',
  invalid_payment_correction: '请填写收款人、收款方式和更正原因，并检查内容长度',
  payment_correction_locked: '冲正记录、已冲正的收款或已作废订单不能更正收款信息',
  no_reversible_payment: '该订单没有可以冲正的收款',
  order_correction_locked: '订单锁定后不能直接修改，请作废后重新录入',
  order_number_exists: '订单号已存在',
  payment_reversal_locked: '已完成或已作废的订单不能冲正收款',
  payment_reversal_required: '订单已有收款，请先冲正收款，再作废订单',
  reason_required: '请填写操作原因',
  version_conflict: '数据已被其他操作更新，请刷新后重试',
};

async function requestError(response: Response): Promise<Error> {
  const detail = await decode(response);
  const code = typeof detail === 'object' && detail !== null && 'code' in detail ? String(detail.code) : '';
  const message = errorCodeMessages[code] ?? statusMessages[response.status] ?? '请求失败';
  return new Error(`${message}（状态码 ${response.status}）`);
}

async function request(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch {
    throw new Error('无法连接服务器，请检查网络后重试');
  }
}

export async function api<T>(path: string): Promise<T> {
  const response = await request(`${base}${path}`, { credentials: 'include' });
  if (!response.ok) throw await requestError(response);
  return decode(response) as Promise<T>;
}

export async function mutate<T>(path: string, method: 'POST' | 'PUT', body?: unknown): Promise<T> {
  const token = await api<{ requestToken: string }>('/api/v1/auth/csrf');
  const response = await request(`${base}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token.requestToken },
    body: JSON.stringify(body ?? {}),
  });
  if (!response.ok) throw await requestError(response);
  return decode(response) as Promise<T>;
}

export async function upload<T>(path: string, file: File): Promise<T> {
  const token = await api<{ requestToken: string }>('/api/v1/auth/csrf');
  const response = await request(`${base}${path}`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/octet-stream', 'X-CSRF-TOKEN': token.requestToken }, body: file });
  if (!response.ok) throw await requestError(response);
  return decode(response) as Promise<T>;
}
